import React from "react";
import { Skeleton } from "./Skeleton";

interface ChartSkeletonProps {
  title?: string;
  height?: string;
  showTitle?: boolean;
  variant?: "line" | "bar";
}

/**
 * Skeleton component for chart loading states.
 * Provides consistent loading UI for all chart components.
 * Supports both line chart and bar chart variants.
 */
export function ChartSkeleton({
  title,
  height = "400px",
  showTitle = true,
  variant = "bar",
}: ChartSkeletonProps) {
  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      {showTitle && (
        <div className="mb-4">
          {title ? (
            <h3 className="text-lg font-semibold">{title}</h3>
          ) : (
            <Skeleton variant="text" height={28} width={200} />
          )}
        </div>
      )}
      <div className="relative" style={{ height }}>
        {/* Chart area skeleton */}
        <div className="absolute inset-0 flex flex-col">
          {/* Y-axis labels */}
          <div className="flex-1 flex items-start pr-2">
            <div className="space-y-8">
              <Skeleton variant="text" height={16} width={30} />
              <Skeleton variant="text" height={16} width={30} />
              <Skeleton variant="text" height={16} width={30} />
              <Skeleton variant="text" height={16} width={30} />
            </div>
          </div>
          {/* Chart grid lines */}
          <div className="flex-1 flex flex-col justify-between px-2 pb-8">
            <div className="border-t border-border w-full" />
            <div className="border-t border-border w-full" />
            <div className="border-t border-border w-full" />
            <div className="border-t border-border w-full" />
          </div>
        </div>
        {/* X-axis labels */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-around px-8 pb-2">
          <Skeleton variant="text" height={16} width={60} />
          <Skeleton variant="text" height={16} width={60} />
          <Skeleton variant="text" height={16} width={60} />
          <Skeleton variant="text" height={16} width={60} />
        </div>
        {/* Chart content placeholder */}
        {variant === "bar" ? (
          // Bar chart skeleton - vertical bars
          <div className="absolute inset-0 flex items-end justify-around px-8 pb-8">
            <Skeleton variant="rectangular" height="60%" width={40} />
            <Skeleton variant="rectangular" height="80%" width={40} />
            <Skeleton variant="rectangular" height="45%" width={40} />
            <Skeleton variant="rectangular" height="70%" width={40} />
          </div>
        ) : (
          // Line chart skeleton - wavy lines
          <div className="absolute inset-0 px-8 pb-8 text-muted-foreground/30">
            <svg className="w-full h-full" viewBox="0 0 400 400" preserveAspectRatio="none">
              {/* First line */}
              <path
                d="M 20 300 Q 100 200, 180 250 T 360 180"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="4 4"
              />
              {/* Second line */}
              <path
                d="M 20 280 Q 100 150, 180 200 T 360 120"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="4 4"
              />
              {/* Dots along the lines */}
              <circle cx="20" cy="300" r="3" fill="currentColor" />
              <circle cx="100" cy="200" r="3" fill="currentColor" />
              <circle cx="180" cy="250" r="3" fill="currentColor" />
              <circle cx="260" cy="220" r="3" fill="currentColor" />
              <circle cx="360" cy="180" r="3" fill="currentColor" />
              <circle cx="20" cy="280" r="3" fill="currentColor" />
              <circle cx="100" cy="150" r="3" fill="currentColor" />
              <circle cx="180" cy="200" r="3" fill="currentColor" />
              <circle cx="260" cy="170" r="3" fill="currentColor" />
              <circle cx="360" cy="120" r="3" fill="currentColor" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}
